import { AppStateType } from 'reducers';
import { createSelector } from 'reselect';
import { keyBy, orderBy } from 'lodash';
import * as constants from '@constants';

import { initialState } from 'ProfileModule/reducers';
import { DefaultBoard, TeamMembershipType } from 'types';

// Matched on exact domain, not substring: `includes('integrations.mosaicapp.com')`
// also matches an attacker-controlled address like `x@integrations.mosaicapp.com.evil.com`.
const SYSTEM_ACCOUNT_EMAIL_DOMAINS = ['integrations.mosaicapp.com', 'maintenance.mosaicapp.com'];

const emailDomain = (email: string) => email.slice(email.lastIndexOf('@') + 1).toLowerCase();

const sortByLastActive = ({
  data,
  orderDirection,
}: {
  data: TeamMembershipType[];
  orderDirection: string;
}) => {
  let sortedData: TeamMembershipType[] = [];
  if (orderDirection === constants.ORDER_DIRECTION.DESC) {
    sortedData = [...data].sort((a, b) => {
      const dateA = a.last_active ? new Date(a.last_active) : null;
      const dateB = b.last_active ? new Date(b.last_active) : null;
      if (!dateA && !dateB) {
        return 0;
      } else if (!dateA) {
        return 1;
      } else if (!dateB) {
        return -1;
      } else {
        return Number(dateB) - Number(dateA);
      }
    });
  } else {
    sortedData = orderBy(
      data,
      constants.TeamMemberColumns.LastActive,
      constants.ORDER_DIRECTION.ASC,
    );
  }

  return sortedData;
};

const sortFnByColumns: Record<string, any> = {
  [constants.TeamMemberColumns.LastActive]: sortByLastActive,
};

const selectProfileReducerRequestStatusesAndErrors = (state: AppStateType) =>
  state.profileReducer.requestStatusesAndErrors || {};

const selectProfileState = (state: AppStateType) => state.profileReducer || initialState;

/* ------------------------------ Team Members ------------------------------ */

export const selectTeamMemberTableFilter = (state: AppStateType) =>
  state.filterReducer[constants.filterIds.teamMemberTable];

const selectTeamMembersState = createSelector(selectProfileState, (state) => state.team_members);

const selectTeamMembersData = createSelector(
  selectTeamMembersState,
  selectProfileReducerRequestStatusesAndErrors,
  (data, requestStatusesAndErrors) => ({
    data,
    isFetchingMember: requestStatusesAndErrors['fetchTeamMembers']?.isRequesting,
    fetchingMemberError: requestStatusesAndErrors['fetchTeamMembers']?.error,

    isUpdatingMember: requestStatusesAndErrors['updateTeamMember']?.isRequesting,
    updateMemberError: requestStatusesAndErrors['updateTeamMember']?.error,
  }),
);

export const selectTeamMembers = createSelector(
  selectTeamMembersData,
  selectTeamMemberTableFilter,
  (
    { data: members, isUpdatingMember, updateMemberError, isFetchingMember, fetchingMemberError },
    filter,
  ) => {
    const filteredData = (members ?? []).filter(
      (member) => !SYSTEM_ACCOUNT_EMAIL_DOMAINS.includes(emailDomain(member.email)),
    );

    const sortFn = sortFnByColumns[filter.orderBy];
    const sortedData: TeamMembershipType[] =
      sortFn?.({ data: filteredData, orderDirection: filter.orderDirection }) ??
      orderBy(filteredData, filter.orderBy, filter.orderDirection);

    const activeMembers = sortedData.filter((member) => !member.is_archived);

    return {
      allMembers: sortedData,
      activeMembers,
      filter,
      isUpdatingMember,
      updateMemberError,
      isFetchingMember,
      fetchingMemberError,
    };
  },
);

export const selectTeamMembersHash = createSelector([selectTeamMembersState], (members) =>
  keyBy(members, 'account_id'),
);

// Server-side paginated view of the team members (GLX-1740). The current page
// lives in `team_members`; no in-memory sort/filter — Rails is authoritative.
export const selectPaginatedTeamMembers = createSelector(
  selectProfileState,
  selectProfileReducerRequestStatusesAndErrors,
  (state, requestStatusesAndErrors) => ({
    members: (state.team_members ?? []) as TeamMembershipType[],
    totalCount: state.team_members_total_count ?? 0,
    isFetching: requestStatusesAndErrors['searchTeamMembers']?.isRequesting,
    error: requestStatusesAndErrors['searchTeamMembers']?.error,
  }),
);

/* ----------------------------- Team Selection ----------------------------- */

export const selectSelectedTargetService = createSelector(
  selectProfileState,
  (state) => state.selectedTargetService,
);

export const selectBoards = createSelector(selectProfileState, (state) => state.default_board);
